import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Tooltip } from '@shopify/polaris';
import { LOCATE_LED_COLORS } from '../utils/eslColors';
import './LocateBayButton.css';

const API_URL = process.env.REACT_APP_API_URL || '';

/**
 * Drawn bulb (Rhys 2026-08-29): OFF by default — grey outline, nothing
 * glowing on a wall of idle cards. While choosing/sending it flashes amber,
 * and once the bay lamp is burning it flashes in the COLOUR that was picked —
 * the button itself is the confirmation, no "U-04 lit yellow" text.
 */
export function BulbGlyph({ lit, colour, size = 18 }) {
  const fill = lit ? (colour || '#fbbf24') : 'none';
  return (
    <svg
      className={lit ? 'LocateBayButton-bulb LocateBayButton-bulb--lit' : 'LocateBayButton-bulb'}
      width={size} height={size} viewBox="0 0 20 20" fill="none"
      stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"
      strokeLinejoin="round" aria-hidden="true"
      style={{
        flexShrink: 0,
        // Glow matches the glass, so a cyan light glows cyan (the CSS class
        // only carries the flash animation).
        filter: lit ? `drop-shadow(0 0 4px ${fill})` : undefined
      }}
    >
      <path
        d="M10 2.6a5.2 5.2 0 0 0-3 9.4c.6.5 1 1.1 1 1.8v.4h4v-.4c0-.7.4-1.3 1-1.8a5.2 5.2 0 0 0-3-9.4Z"
        fill={fill}
        stroke={lit ? 'rgba(0,0,0,0.4)' : 'currentColor'}
      />
      <path d="M8.2 16.4h3.6M9 18.3h2" />
    </svg>
  );
}

/**
 * "Locate" — lights the bay lamp for this product's W/H location so staff can
 * walk straight to it (Rhys 2026-08-29).
 *
 * Colours are the ones pickers CANNOT choose (white / yellow / cyan), so a
 * locate light is never mistaken for someone's live pick light. Tap the
 * button to open the swatches, tap a colour to light it.
 *
 * Fails quietly and informatively: a location with no lamp, or a bay already
 * lit for an active batch, reports back rather than disturbing a picker.
 * Success shows NO text — the bulb flashes the chosen colour for the minute
 * the bay lamp burns.
 *
 * Styling lives in LocateBayButton.css and mirrors LocationMapBadge.css: the
 * two sit side by side on the product card and have to read as one family.
 * Dark mode is CSS-only for the same reason — no darkMode prop to drift.
 */
export default function LocateBayButton({ location, square = false }) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // CSS colour of the burning bay lamp; null once it expires.
  const [litColour, setLitColour] = useState(null);
  const timers = useRef({ lit: null, err: null });

  useEffect(() => () => {
    clearTimeout(timers.current.lit);
    clearTimeout(timers.current.err);
  }, []);

  if (!location) return null;

  const LIT_SECONDS = 60;

  const light = async (colour) => {
    setBusy(true);
    setError('');
    try {
      await axios.post(`${API_URL}/api/esl/locate-bay`,
        { location, ledColor: colour.value, seconds: LIT_SECONDS },
        { withCredentials: true });
      // The button is the confirmation: flash the picked colour for as long
      // as the bay lamp burns.
      setLitColour(colour.css);
      setOpen(false);
      clearTimeout(timers.current.lit);
      timers.current.lit = setTimeout(() => setLitColour(null), LIT_SECONDS * 1000);
    } catch (err) {
      const code = err.response?.data?.error;
      const text = code === 'bay_in_use_by_picker' ? 'A picker is using that bay right now'
        : code === 'no_lamp_at_bay' ? 'No lamp on that bay'
          : code === 'unrecognised_location' ? "Can't read that location"
            : code === 'pick_to_light_disabled' ? 'Pick-to-light is off'
              : (err.response?.data?.error || 'Locate failed');
      setError(text);
      setOpen(false);
      clearTimeout(timers.current.err);
      timers.current.err = setTimeout(() => setError(''), 5000);
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <span className="LocateBayButton-status LocateBayButton-status--err">
        {error}
      </span>
    );
  }

  if (open) {
    return (
      <span className="LocateBayButton-swatches">
        <BulbGlyph lit />
        {LOCATE_LED_COLORS.map((colour) => (
          <Tooltip key={colour.value} content={`Light ${location} ${colour.label.toLowerCase()}`}>
            <button
              type="button"
              className="LocateBayButton-swatch"
              aria-label={`Locate in ${colour.label}`}
              disabled={busy}
              onClick={(e) => { e.stopPropagation(); light(colour); }}
              style={{ background: colour.css }}
            />
          </Tooltip>
        ))}
        <button
          type="button"
          className="LocateBayButton-cancel"
          aria-label="Cancel locate"
          onClick={(e) => { e.stopPropagation(); setOpen(false); }}
        >
          ✕
        </button>
      </span>
    );
  }

  return (
    <Tooltip content={litColour
      ? `Bay lamp at ${location} is burning — click to light it again`
      : `Light the bay lamp at ${location} for ${LIT_SECONDS}s`}>
      <button
        type="button"
        className={`LocateBayButton${square ? ' LocateBayButton--square' : ''}${litColour ? ' LocateBayButton--burning' : ''}`}
        disabled={busy}
        // The whole button pulses in the lamp's colour while it burns.
        style={litColour ? { '--locate-colour': litColour } : undefined}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
      >
        <span className="LocateBayButton-action" aria-hidden style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <BulbGlyph lit={busy || Boolean(litColour)} colour={litColour} />
          {!square && 'Locate'}
        </span>
      </button>
    </Tooltip>
  );
}
